/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IFileUploadQueue,
  IFlipDirection,
  IJobType,
  ILicense,
  IWood,
  IProbeForZHeightCommand,
  ISoftwareUsed,
  IToolSetter,
  IUnits,
  IXYDatumPosition,
  IZZeroPosition,
  IPostAttachment,
  IControllerType,
  IModelGroup,
  IAttachmentCategory,
} from './post.d';
import { getControllerFromFileName } from './post.slice.helpers';
import { CONTROLLER_CONFIG } from '@/lib/controllerConfig';

const initialState = {
  projectEditorData: {
    _id: '',
    models: [] as IPostAttachment[],
    mediaFiles: [] as IPostAttachment[],
    thumbnail: null as IPostAttachment | null,
    title: '',
    description: '',
    category: '',
    difficulty: '',
    tags: [] as string[],
    originalMachine: '',
    supportedMachines: [] as string[],
    softwareUsed: [Object.values(ISoftwareUsed)[0]],
    materialUsed: IWood.WOOD_HARD,
    jobType: Object.values(IJobType)[0],
    flipDirection: '' as IFlipDirection | '',
    units: Object.values(IUnits)[0],
    jobSizeHeight: 0.25,
    jobSizeThickness: 0.25,
    jobSizeWidth: 0.25,
    jobSizeDiameter: 0,
    jobSizeLength: 0,
    zZeroPosition: Object.values(IZZeroPosition)[0],
    xyDatumPosition: Object.values(IXYDatumPosition)[0],
    toolSetter: Object.values(IToolSetter)[1],
    probeForZHeightCommand: Object.values(IProbeForZHeightCommand)[0],
    isDimensionalMaterial: false,
    dimensionalMaterialLinks: [] as string[],
    youtubeAttachments: [],
    licenceType: Object.values(ILicense)[0],
    isLoading: false,
    isError: false,
    isSuccess: false,
    error: null,
    fileUploadQueue: [] as IFileUploadQueue[],
    selectedControllers: [] as IControllerType[],
    selectedModelIds: [] as string[],
    selectedGroupIds: [] as string[],
    modelGroups: [] as IModelGroup[],
  },
};

const projectEditorSlice = createSlice({
  name: 'projectEditor',
  initialState,
  reducers: {
    resetProjectEditorData: (state) => {
      state.projectEditorData = initialState.projectEditorData;
    },
    setProjectEditorData: (state, action) => {
      state.projectEditorData = {
        ...state.projectEditorData,
        ...action.payload,
      };
    },
    addTagToProjectEditorData: (state, action: PayloadAction<string>) => {
      if (!state.projectEditorData.tags) {
        state.projectEditorData.tags = [];
      }
      if (!state.projectEditorData.tags.includes(action.payload)) {
        state.projectEditorData.tags.push(action.payload);
      }
    },
    removeFileFromUploadQueueByCategory: (state, action) => {
      state.projectEditorData = {
        ...state.projectEditorData,
        fileUploadQueue: state.projectEditorData.fileUploadQueue.filter(
          (file) => file.category !== action.payload
        ),
      };
    },
    removeFileFromUploadQueue: (state, action) => {
      const newFileUploadQueue = state.projectEditorData.fileUploadQueue.filter(
        (file) =>
          !(
            file.fileHash === action.payload.fileHash &&
            file.fileName === action.payload.fileName &&
            file.fileSize === action.payload.fileSize &&
            file.category === action.payload.category
          )
      );
      state.projectEditorData = {
        ...state.projectEditorData,
        fileUploadQueue: newFileUploadQueue,
      };
    },
    removeAllFilesFromUploadQueue: (state) => {
      state.projectEditorData = {
        ...state.projectEditorData,
        fileUploadQueue: [],
      };
    },
    addFileToUploadQueue: (state, action) => {
      // Use fileHash + fileName + fileSize as unique identifier to allow same content with different names
      const existingFile = state.projectEditorData.fileUploadQueue.find(
        (file) =>
          file.fileHash === action.payload.fileHash &&
          file.fileName === action.payload.fileName &&
          file.fileSize === action.payload.fileSize &&
          file.category === action.payload.category
      );
      if (existingFile) {
        state.projectEditorData.fileUploadQueue =
          state.projectEditorData.fileUploadQueue.filter(
            (file) =>
              !(
                file.fileHash === action.payload.fileHash &&
                file.fileName === action.payload.fileName &&
                file.fileSize === action.payload.fileSize &&
                file.category === action.payload.category
              )
          );
      }
      state.projectEditorData.fileUploadQueue.push(action.payload);
    },
    setSelectedControllers: (state, action) => {
      state.projectEditorData.selectedControllers = action.payload;
    },
    toggleController: (state, action) => {
      const controller = action.payload;
      const isSelected =
        state.projectEditorData.selectedControllers.includes(controller);
      if (isSelected) {
        state.projectEditorData.selectedControllers =
          state.projectEditorData.selectedControllers.filter(
            (c) => c !== controller
          );
      } else {
        state.projectEditorData.selectedControllers.push(controller);
      }
    },
    toggleModelSelection: (state, action) => {
      const modelId = action.payload;
      const isSelected =
        state.projectEditorData.selectedModelIds.includes(modelId);
      if (isSelected) {
        state.projectEditorData.selectedModelIds =
          state.projectEditorData.selectedModelIds.filter(
            (id) => id !== modelId
          );
      } else {
        state.projectEditorData.selectedModelIds.push(modelId);
      }
    },
    selectAllModels: (state) => {
      const allModelIds = state.projectEditorData.models.map(
        (model) => model._id
      );
      state.projectEditorData.selectedModelIds = allModelIds;
    },
    clearModelSelection: (state) => {
      state.projectEditorData.selectedModelIds = [];
    },
    toggleGroupSelection: (state, action) => {
      const groupId = action.payload;
      const isSelected =
        state.projectEditorData.selectedGroupIds.includes(groupId);
      if (isSelected) {
        state.projectEditorData.selectedGroupIds =
          state.projectEditorData.selectedGroupIds.filter(
            (id) => id !== groupId
          );
      } else {
        state.projectEditorData.selectedGroupIds.push(groupId);
      }
    },
    clearGroupSelection: (state) => {
      state.projectEditorData.selectedGroupIds = [];
    },
    createModelGroup: (state, action) => {
      const { modelIds, toolData, name } = action.payload;
      const groupNumber = state.projectEditorData.modelGroups.length + 1;
      const newGroup: IModelGroup = {
        _id: Date.now().toString(),
        name: name || `Group ${groupNumber}`,
        modelIds: [...modelIds].sort(), // Sort to ensure consistent grouping
        toolData: toolData || [],
        createdAt: new Date().toISOString(),
      };
      state.projectEditorData.modelGroups.push(newGroup);
    },
    updateModelGroup: (state, action) => {
      const { groupId, toolData } = action.payload;
      const groupIndex = state.projectEditorData.modelGroups.findIndex(
        (group) => group._id === groupId
      );
      if (groupIndex !== -1) {
        state.projectEditorData.modelGroups[groupIndex].toolData = toolData;
      }
    },
    addToolDataToGroup: (state, action) => {
      const { groupId, toolData } = action.payload;
      const groupIndex = state.projectEditorData.modelGroups.findIndex(
        (group) => group._id === groupId
      );
      if (groupIndex !== -1) {
        state.projectEditorData.modelGroups[groupIndex].toolData.push(toolData);
      }
    },
    removeToolDataFromGroup: (state, action) => {
      const { groupId, toolDataId } = action.payload;
      const groupIndex = state.projectEditorData.modelGroups.findIndex(
        (group) => group._id === groupId
      );
      if (groupIndex !== -1) {
        state.projectEditorData.modelGroups[groupIndex].toolData =
          state.projectEditorData.modelGroups[groupIndex].toolData.filter(
            (tool) => tool._id !== toolDataId
          );
      }
    },
    deleteModelGroup: (state, action) => {
      const groupId = action.payload;
      state.projectEditorData.modelGroups =
        state.projectEditorData.modelGroups.filter(
          (group) => group._id !== groupId
        );
    },
  },
});

export type IProjectEditorState = typeof initialState.projectEditorData;

export const {
  setProjectEditorData,
  addTagToProjectEditorData,
  resetProjectEditorData,
  removeFileFromUploadQueue,
  addFileToUploadQueue,
  removeAllFilesFromUploadQueue,
  removeFileFromUploadQueueByCategory,
  setSelectedControllers,
  toggleController,
  toggleModelSelection,
  selectAllModels,
  clearModelSelection,
  toggleGroupSelection,
  clearGroupSelection,
  createModelGroup,
  updateModelGroup,
  addToolDataToGroup,
  removeToolDataFromGroup,
  deleteModelGroup,
} = projectEditorSlice.actions;

export default projectEditorSlice.reducer;

export const selectProjectEditorData = (state: {
  projectEditor: {
    projectEditorData: IProjectEditorState;
  };
}) => {
  return state.projectEditor.projectEditorData;
};

// Selector to get files for a specific controller (existing models + upload queue)
export const selectFilesForController =
  (controller: IControllerType) =>
  (state: {
    projectEditor: {
      projectEditorData: IProjectEditorState;
    };
  }) => {
    const { models, fileUploadQueue } = state.projectEditor.projectEditorData;

    const uploadQueueModels = fileUploadQueue.filter(
      (file) =>
        file.category === IAttachmentCategory.postModel &&
        file.controller === controller
    );

    // Categorize existing models by filename and controller property
    const existingModels = models.filter((model) => {
      if (model.category !== IAttachmentCategory.postModel) return false;

      // Direct controller match
      if (model.controller === controller) return true;

      // Case-insensitive match
      if (model.controller?.toLowerCase() === controller.toLowerCase())
        return true;

      // Check if controller name matches (for backward compatibility)
      const config = CONTROLLER_CONFIG[controller];
      if (model.controller === config.name) return true;

      // Categorize by filename if no controller property
      if (!model.controller && model.fileName) {
        const fileNameController = getControllerFromFileName(model.fileName);
        return fileNameController === controller;
      }

      // If model has no controller property and no filename, default to OTHER
      if (!model.controller && !model.fileName) {
        return controller === IControllerType.OTHER;
      }

      return false;
    });

    return [...existingModels, ...uploadQueueModels];
  };

// Selector to get controllers that have existing models
export const selectControllersFromExistingModels = (state: {
  projectEditor: {
    projectEditorData: IProjectEditorState;
  };
}) => {
  const { models } = state.projectEditor.projectEditorData;
  const existingModels = models.filter(
    (model) => model.category === IAttachmentCategory.postModel
  );

  const controllers = new Set<IControllerType>();
  let hasUncategorizedModels = false;

  existingModels.forEach((model) => {
    // If model has controller property, use it
    if (model.controller) {
      const controller = Object.values(IControllerType).find(
        (c) =>
          c === model.controller ||
          (model.controller &&
            c.toLowerCase() === model.controller.toLowerCase()) ||
          CONTROLLER_CONFIG[c]?.name === model.controller
      );
      if (controller) {
        controllers.add(controller);
      } else {
        // If controller property doesn't match any known controller, treat as uncategorized
        hasUncategorizedModels = true;
      }
    }
    // Otherwise, try to determine from filename
    else if (model.fileName) {
      const fileNameController = getControllerFromFileName(model.fileName);
      controllers.add(fileNameController);
    }
    // If no controller property and no filename, treat as uncategorized
    else {
      hasUncategorizedModels = true;
    }
  });

  // Add OTHER controller if there are any uncategorized models
  if (hasUncategorizedModels) {
    controllers.add(IControllerType.OTHER);
  }

  return Array.from(controllers);
};
